<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/history_drivers/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.history_drivers')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="ml-12">
    <div class="ml-12">
      <div
        v-if="!showStatistics"
        class="flex justify-center"
        style="padding: 2rem;"
      >
        <Alert
          variant="info"
          :title="$t('pages.history.extraFiltersPlaceholderTitle')"
          :sub-title="$t('pages.history.extraFiltersPlaceholderDriver')"
        />
      </div>
      <div
        v-if="showStatistics"
        class="ml-12 flex items-center justify-end"
        style="justify-content: flex-end; padding: 1rem 2rem 1rem 1rem;"
      >
        <Switch
          v-model="showDiagram"
          :label="$t('pages.history.showDiagram')"
        />
      </div>
    </div>
    <HistoryDriverDifferenceStatisticsTable
      v-if="showStatistics && !showDiagram"
      :provider-params="providerParams"
    />
    <HistoryChart
      v-if="showStatistics && showDiagram"
      :raw-data="records"
      data-type="driver"
      :visible-columns="visibleColumns"
      :provider-params="providerParams"
    />
    <div class="table">
      <data-table
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        class="mb-auto"
        :busy="loadingTable"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
    :per-page-choices="[5, 10, 50, 100, 1000, 10000]"
  />
</template>

<script lang="ts" setup>
import {
  computed,
  reactive,
  ref,
  unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import { format } from 'date-fns';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import HistoryDriverDifferenceStatisticsTable
  from '@utils/HistoryDriverDifferenceStatisticsTable.vue';
import Switch from '@utils/inputs/Switch.vue';
import Alert from '@utils/Alert.vue';
import HistoryChart from '@utils/chart/HistoryChart.vue';
import {
  DriverHistoryType,
  type HistoryDriver,
  type HydraCollection,
  type Region,
} from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef<number>('page', 1);
const perPage = useQueryRef<number>('perPage', 10);
const filter = useQueryRef<any>('filter', {});

const totalFilter = computed(() => ({
  ...filter.value,
}));

const sortKey = useQueryRef<string | undefined>('sortKey', 'recordingDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.history.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'recordingDate',
    title: t('pages.history.recordingDate'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.history.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.history.filterGroups.general'),
      options: {
        label: t('pages.history.recordingDate'),
      },

    },
  },
  {
    key: 'exists[region]',
    title: t('pages.history.overall'),
    group: t('pages.history.fieldGroups.general'),
    sortable: false,
    visible: false,
    computed: (item: HistoryDriver) => ((item.region === null) ? t('yes') : t('no')),
    filter: {
      type: 'dropdown',
      group: t('pages.history.filterGroups.general'),
      options: {
        options: [
          {
            value: false,
            label: t('yes'),
          },
          {
            value: true,
            label: t('no'),
          },
        ],
        label: t('pages.history.overall'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('campaign.region'),
    group: t('pages.history.fieldGroups.general'),
    sortable: true,
    visible: false,
    computed: (item: HistoryDriver) => ((item?.region === null) ? t('pages.history.overall') : item.region?.name) ?? '',
    filter: {
      key: 'region',
      type: 'tags',
      group: t('pages.history.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('campaign.region'),
        placeholder: t('select2', { what: t('campaign.region2') }),
      },
    },
  },
  {
    key: 'driverType',
    csvTranslations: {
      Incomplete: t('history.driverHistoryType.INCOMPLETE'),
      Standard: t('history.driverHistoryType.STANDARD'),
      Business: t('history.driverHistoryType.BUSINESS'),
      Mixed: t('history.driverHistoryType.MIXED'),
      All: t('history.driverHistoryType.ALL'),
    },
    title: t('pages.history.driverType'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    formatter: (item: DriverHistoryType) => t(`history.driverHistoryType.${item.toUpperCase()}`),
    filter: {
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('history.driverHistoryType.INCOMPLETE'),
            value: DriverHistoryType.INCOMPLETE,
          },
          {
            label: t('history.driverHistoryType.STANDARD'),
            value: DriverHistoryType.STANDARD,
          },
          {
            label: t('history.driverHistoryType.BUSINESS'),
            value: DriverHistoryType.BUSINESS,
          },
          {
            label: t('history.driverHistoryType.MIXED'),
            value: DriverHistoryType.MIXED,
          },
          {
            label: t('history.driverHistoryType.ALL'),
            value: DriverHistoryType.ALL,
          },
        ],
        label: t('pages.history.driverType'),
      },
    },
  },
  {
    key: 'count',
    title: t('pages.history.count'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.count'),
      },
    },
  },
  {
    key: 'countDeleted',
    title: t('pages.history.countDeleted'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countDeleted'),
      },
    },
  },
  {
    key: 'countEquipped',
    title: t('pages.history.countEquipped'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countEquipped'),
      },
    },
  },
  {
    key: 'countV1Box',
    title: t('pages.history.countV1Box'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV1Box'),
      },
    },
  },
  {
    key: 'countV2Box',
    title: t('pages.history.countV2Box'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2Box'),
      },
    },
  },
  {
    key: 'countV2MountedBox',
    title: t('pages.history.countV2MountedBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2MountedBox'),
      },
    },
  },
  {
    key: 'countV2InitialBox',
    title: t('pages.history.countV2InitialBox'),
    sortable: true,
    visible: false,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2InitialBox'),
      },
    },
  },
  {
    key: 'countV2ReturnedBox',
    title: t('pages.history.countV2ReturnedBox'),
    sortable: true,
    visible: false,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2ReturnedBox'),
      },
    },
  },
  {
    key: 'countV2LostBox',
    title: t('pages.history.countV2LostBox'),
    sortable: true,
    visible: false,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2LostBox'),
      },
    },
  },
  {
    key: 'countV2DestroyedBox',
    title: t('pages.history.countV2DestroyedBox'),
    sortable: true,
    visible: false,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2DestroyedBox'),
      },
    },
  },
  {
    key: 'countV2OtherBox',
    title: t('pages.history.countV2OtherBox'),
    sortable: true,
    visible: false,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countV2OtherBox'),
      },
    },
  },
  {
    key: 'countInitialBox',
    title: t('pages.history.countInitialBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countInitialBox'),
      },
    },
  },
  {
    key: 'countReturnedBox',
    title: t('pages.history.countReturnedBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countReturnedBox'),
      },
    },
  },
  {
    key: 'countLostBox',
    title: t('pages.history.countLostBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countLostBox'),
      },
    },
  },
  {
    key: 'countDestroyedBox',
    title: t('pages.history.countDestroyedBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countDestroyedBox'),
      },
    },
  },
  {
    key: 'countMountedBox',
    title: t('pages.history.countMountedBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countMountedBox'),
      },
    },
  },
  {
    key: 'countOtherBox',
    title: t('pages.history.countOtherBox'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countOtherBox'),
      },
    },
  },
  {
    key: 'countBoxWithBase',
    title: t('pages.history.countBoxWithBase'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBoxWithBase'),
      },
    },
  },
  {
    key: 'countBoxNoBase',
    title: t('pages.history.countBoxNoBase'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBoxNoBase'),
      },
    },
  },
  {
    key: 'countBoxUnknown',
    title: t('pages.history.countBoxUnknown'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBoxUnknown'),
      },
    },
  },
  {
    key: 'countPhase1',
    title: t('pages.history.countPhase1'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countPhase1'),
      },
    },
  },
  {
    key: 'countPhase2',
    title: t('pages.history.countPhase2'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countPhase2'),
      },
    },
  },
  {
    key: 'countPhase3',
    title: t('pages.history.countPhase3'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countPhase3'),
      },
    },
  },
  {
    key: 'countPhase0',
    title: t('pages.history.countPhase0'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countPhase0'),
      },
    },
  },
  {
    key: 'countBtRace',
    title: t('pages.history.countBtRace'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBtRace'),
      },
    },
  },
  {
    key: 'countBtSingleSpeed',
    title: t('pages.history.countBtSingleSpeed'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBtSingleSpeed'),
      },
    },
  },
  {
    key: 'countBtCityBike',
    title: t('pages.history.countBtCityBike'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBtCityBike'),
      },
    },
  },
  {
    key: 'countBtECityBike',
    title: t('pages.history.countBtECityBike'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBtECityBike'),
      },
    },
  },
  {
    key: 'countBtEMountainBike',
    title: t('pages.history.countBtEMountainBike'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countBtEMountainBike'),
      },
    },
  },
  {
    key: 'countIncompleteBusiness',
    title: t('pages.history.countIncompleteBusiness'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countIncompleteBusiness'),
      },
    },
  },
  {
    key: 'countIncompleteStandard',
    title: t('pages.history.countIncompleteStandard'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('pages.history.countIncompleteStandard'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
} = useProvider<HydraCollection<HistoryDriver>>('/history_drivers', providerParams);

const showDiagram = ref(false);

// eslint-disable-next-line max-len
const showStatistics = computed(() =>
  (providerParams?.filter?.driverType
    && (providerParams?.filter?.region?.length > 0 || (providerParams?.filter?.['exists[region]'] === false)))
  && !(providerParams?.filter?.region?.length > 0 && providerParams?.filter?.['exists[region]'] === false));

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

</script>
<style lang="scss">
.table {
  @apply flex flex-col;
}
</style>
